import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({ baseURL: API_URL, headers: { 'Content-Type': 'application/json' }, timeout: 10000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth
export const adminLogin = async (email, password) => (await api.post('/auth/admin/login', { email, password })).data;
export const changeAdminPassword = async (currentPassword, newPassword) => (await api.patch('/auth/admin/password', { currentPassword, newPassword })).data; 
export const updateAdminProfile = async (data) => (await api.patch('/auth/admin/profile', data)).data;

// Clients
export const getClients = async () => (await api.get('/clients')).data;
export const createClient = async (data) => (await api.post('/clients', data)).data;
export const updateClient = async (id, data) => (await api.put(`/clients/${id}`, data)).data;
export const deleteClient = async (id) => (await api.delete(`/clients/${id}`)).data;

// Create an invite token for a client. Returns { inviteToken, expiresAt }
export const createInvite = async (id, expiresInHours = 168) => (await api.post(`/clients/${id}/invite`, { expiresInHours })).data;
export const getUsers = async (params = {}) => (await api.get('/users', { params })).data;
export const createStaffUser = async (data) => (await api.post('/users/staff', data)).data;
export const createStaffInvite = async (data) => (await api.post('/users/staff/invite', data)).data;
export const resendStaffInvite = async (userId, expiresInHours = 168) => (await api.post(`/users/staff/${userId}/invite-resend`, { expiresInHours })).data;
export const cancelStaffInvite = async (userId) => (await api.post(`/users/staff/${userId}/invite-cancel`)).data;
export const acceptStaffInvite = async (email, inviteToken, password) => (await axios.post(`${API_URL}/auth/staff/accept-invite`, { email, inviteToken, password }, { headers: { 'Content-Type': 'application/json' } })).data;

// Invoices
export const getInvoices = async () => (await api.get('/invoices')).data;
export const createInvoice = async (data) => (await api.post('/invoices', data)).data;export const updateInvoice = async (id, data) => (await api.put(`/invoices/${id}`, data)).data;export const updateInvoiceStatus = async (id, status) => (await api.patch(`/invoices/${id}/status`, { status })).data;
export const reviseInvoice = async (id, data) => (await api.post(`/invoices/${id}/revise`, data)).data;
export const sendInvoice = async (id, body = {}) => (await api.post(`/invoices/${id}/send`, body)).data;
export const deleteInvoice = async (id) => (await api.delete(`/invoices/${id}`)).data;

// Leads
export const getLeads = async () => (await api.get('/leads')).data;
export const updateLeadStatus = async (id, payload) => {
  const body = typeof payload === 'string' ? { status: payload } : (payload || {});
  return (await api.patch(`/leads/${id}/status`, body)).data;
};
export const deleteLead = async (id) => (await api.delete(`/leads/${id}`)).data;
export const scanForLeads = async (location, industry, options = {}) => (
  await api.post('/leads/scan', { location, industry, ...options })
).data;

// Settings
export const getSettings = async () => (await api.get('/settings')).data;
export const updateSettings = async (data) => (await api.put('/settings', data)).data;

// Proposals
export const getProposals = async () => (await api.get('/proposals')).data;
export const getProposal = async (id) => (await api.get(`/proposals/${id}`)).data;
export const createProposal = async (data) => (await api.post('/proposals', data)).data;
export const updateProposal = async (id, data) => (await api.put(`/proposals/${id}`, data)).data;
export const reviseProposal = async (id, data) => (await api.post(`/proposals/${id}/revise`, data)).data;
export const updateProposalStatus = async (id, status) => (await api.patch(`/proposals/${id}/status`, { status })).data;
export const sendProposal = async (id, body = {}) => (await api.post(`/proposals/${id}/send`, body)).data;
export const convertProposal = async (id) => (await api.post(`/proposals/${id}/convert`)).data;
export const deleteProposal = async (id) => (await api.delete(`/proposals/${id}`)).data;
export const getProposalContentTemplates = async () => (await api.get('/proposal-content-templates')).data;
export const createProposalContentTemplate = async (data) => (await api.post('/proposal-content-templates', data)).data;
export const updateProposalContentTemplate = async (id, data) => (await api.put(`/proposal-content-templates/${id}`, data)).data;
export const deleteProposalContentTemplate = async (id) => (await api.delete(`/proposal-content-templates/${id}`)).data;

// Dashboard
export const getDashboardStats = async () => (await api.get('/dashboard/stats')).data;
export const getNotificationSummary = async (params = {}) => (await api.get('/notifications/summary', { params })).data;
export const getFinancialSummary = async (params = {}) => (await api.get('/financials/summary', { params })).data;
export const getExpenses = async (params = {}) => (await api.get('/financials/expenses', { params })).data;
export const createExpense = async (data) => (await api.post('/financials/expenses', data)).data;
export const deleteExpense = async (id) => (await api.delete(`/financials/expenses/${id}`)).data;
export const getTeamPayouts = async (params = {}) => (await api.get('/financials/team-payouts', { params })).data;
export const createTeamPayout = async (data) => (await api.post('/financials/team-payouts', data)).data;
export const updateTeamPayoutStatus = async (id, status) => (await api.patch(`/financials/team-payouts/${id}/status`, { status })).data;
export const deleteTeamPayout = async (id) => (await api.delete(`/financials/team-payouts/${id}`)).data;
export const getTaxRemittances = async (params = {}) => (await api.get('/financials/tax-remittances', { params })).data;
export const createTaxRemittance = async (data) => (await api.post('/financials/tax-remittances', data)).data;
export const deleteTaxRemittance = async (id) => (await api.delete(`/financials/tax-remittances/${id}`)).data;
export const getContractors = async (params = {}) => (await api.get('/financials/contractors', { params })).data;
export const createContractor = async (data) => (await api.post('/financials/contractors', data)).data;
export const updateContractor = async (id, data) => (await api.patch(`/financials/contractors/${id}`, data)).data;
export const archiveContractor = async (id) => (await api.delete(`/financials/contractors/${id}`)).data;
export const getTimeEntries = async (params = {}) => (await api.get('/financials/time-entries', { params })).data;
export const createTimeEntry = async (data) => (await api.post('/financials/time-entries', data)).data;
export const updateTimeEntry = async (id, data) => (await api.patch(`/financials/time-entries/${id}`, data)).data;
export const deleteTimeEntry = async (id) => (await api.delete(`/financials/time-entries/${id}`)).data;
export const autoDraftTeamPayouts = async (data = {}) => (await api.post('/financials/team-payouts/auto-draft', data)).data;
export const syncStripeFeeExpenses = async (data = {}) => (await api.post('/financials/expenses/sync-stripe-fees', data)).data;
export const getFinancialReminders = async (params = {}) => (await api.get('/financials/reminders', { params })).data;
export const runMonthlyFinancialReminders = async () => (await api.post('/financials/reminders/run-monthly')).data;
export const resolveFinancialReminder = async (id) => (await api.patch(`/financials/reminders/${id}/resolve`)).data;
export const downloadFinancialCsv = async (resource, params = {}) => {
  const response = await api.get(`/financials/exports/${resource}.csv`, { params, responseType: 'blob' });
  return response.data;
};

// Projects
export const getProjects = async () => (await api.get('/projects')).data;
export const getProject = async (id) => (await api.get(`/projects/${id}`)).data;
export const createProject = async (data) => (await api.post('/projects', data)).data;
export const updateProject = async (id, data) => (await api.put(`/projects/${id}`, data)).data;
export const deleteProject = async (id) => (await api.delete(`/projects/${id}`)).data;
export const requestProjectReview = async (id, payload = {}) => (await api.post(`/projects/${id}/request-review`, payload)).data;
export const getClientProjects = async (clientId) => (await api.get(`/clients/${clientId}/projects`)).data;

// Messages
export const getProjectMessages = async (projectId) => (await api.get(`/messages/project/${projectId}`)).data;
export const sendMessage = async (data) => {
  // When sending FormData (multipart), let axios set headers automatically
  if (data instanceof FormData) {
    const response = await api.post('/messages', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  }
  return (await api.post('/messages', data)).data;
};
export const markMessageRead = async (messageId) => (await api.patch(`/messages/${messageId}/read`)).data;
export const deleteMessage = async (messageId) => (await api.delete(`/messages/${messageId}`)).data;

// Documents
export const getDocuments = async (filters = {}) => (await api.get('/documents', { params: filters })).data;
export const getClientDocuments = async (clientId) => (await api.get(`/clients/${clientId}/documents`)).data;
export const uploadClientDocument = async (clientId, formData) => (await api.post(`/clients/${clientId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
export const deleteClientDocument = async (clientId, docId) => (await api.delete(`/clients/${clientId}/documents/${docId}`)).data;
export const deleteDocument = async (docId) => (await api.delete(`/documents/${docId}`)).data;
export const restoreDocument = async (docId) => (await api.post(`/documents/${docId}/restore`)).data;
export const patchDocument = async (docId, data) => (await api.patch(`/documents/${docId}`, data)).data;
export const getInvoiceDocuments = async (invoiceId) => (await api.get(`/invoices/${invoiceId}/documents`)).data;
export const getProposalDocuments = async (proposalId) => (await api.get(`/proposals/${proposalId}/documents`)).data;
export const getInvoicePayments = async (invoiceId) => (await api.get(`/invoices/${invoiceId}/payments`)).data;
export const refundPayment = async (paymentId, amount) => (await api.post(`/payments/${paymentId}/refund`, { amount })).data;
export const createInvoiceCheckout = async (invoiceId, amount) => (await api.post(`/invoices/${invoiceId}/checkout`, amount ? { amount } : {})).data; 
export const recordManualInvoicePayment = async (invoiceId, amount) => (await api.post(`/invoices/${invoiceId}/manual-payment`, amount === undefined ? {} : { amount })).data;
export const getReviews = async (params = {}) => (await api.get('/reviews', { params })).data;
export const updateReviewStatus = async (id, status) => (await api.patch(`/reviews/${id}/status`, { status })).data;

export default api;
